import React, { useEffect, useState } from 'react';
import { getRecurringGroupApi, updateRecurringStatusApi, getRecurringRidesApi } from '../../services/auth/recurringApis';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export const ApprovalPanel = ({ recurrenceGroupId }) => {
    const navigate = useNavigate();
    const [groupData, setGroupData] = useState(null);
    const [rides, setRides] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            const gData = await getRecurringGroupApi(recurrenceGroupId);
            const rData = await getRecurringRidesApi(recurrenceGroupId);
            setGroupData(gData);
            setRides(rData);
            setLoading(false);
        };
        if (recurrenceGroupId) {
            fetchAll();
        }
    }, [recurrenceGroupId]);

    const handleUpdateStatus = async (userId, targetStatus) => {
        // Optimistic UI Update
        setGroupData(prev => {
            if (!prev) return prev;
            const newRequests = prev.requests.map(req => {
                if (req.userId === userId) {
                    return { ...req, status: targetStatus };
                }
                return req;
            });
            return { ...prev, requests: newRequests };
        });

        const success = await updateRecurringStatusApi(recurrenceGroupId, userId, targetStatus);
        if (!success) {
             // Rollback not implemented for simplicity as instructed optimistic UI requirement
             // Usually we would fallback if it fails
             const gData = await getRecurringGroupApi(recurrenceGroupId);
             setGroupData(gData);
        }
    };

    if (loading) return <div className="text-sm p-4 text-gray-500">Loading recurring details...</div>;

    const requests = groupData?.requests || [];

    return (
        <div className="mt-6 flex flex-col space-y-4 bg-white p-4 rounded-xl border border-zinc-200">
            <h3 className="text-xl font-bold">Recurring Ride Management</h3>
            
            {/* Calendar / Accordion Dates Fallback */}
            <details className="cursor-pointer border border-zinc-200 rounded-lg p-3 bg-zinc-50" open>
                <summary className="font-semibold outline-none">
                    Scheduled Dates ({rides.length})
                </summary>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {rides.map(ride => (
                        <div 
                            key={ride.id} 
                            onClick={() => navigate(`/my-rides/${ride.id}`)}
                            className="text-sm bg-white p-2 text-center rounded border border-zinc-200 shadow-sm cursor-pointer hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-colors"
                        >
                            {new Date(ride.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </div>
                    ))}
                </div>
            </details>

            {/* Approval Panel */}
            <div className="flex flex-col space-y-3 mt-4">
                <h4 className="font-semibold text-lg">Group Requests ({requests.length})</h4>
                {requests.length === 0 ? (
                    <div className="text-sm text-gray-500 italic">No recurring requests yet.</div>
                ) : (
                    requests.map(req => (
                        <div key={req.id} className="flex justify-between items-center p-3 border border-zinc-200 rounded-lg shadow-sm">
                            <div className="flex flex-col">
                                <span className="font-medium text-black">{req.user?.name || 'User'}</span>
                                <span className="text-xs text-gray-500">
                                    Requested: {new Date(req.requestedAt).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className={`text-xs px-2 py-1 rounded font-semibold ${req.status === 'APPROVED' ? 'bg-green-100 text-green-700' : req.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                    {req.status}
                                </span>
                                {req.status === 'PENDING' && (
                                    <>
                                        <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" onClick={() => handleUpdateStatus(req.userId, 'APPROVED')}>Approve</Button>
                                        <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleUpdateStatus(req.userId, 'REJECTED')}>Reject</Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
